import { useState } from "react";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Edit,
  ShoppingCart,
  Trash2,
} from "lucide-react";

interface Item {
  _id: string;
  name: string;
  category: string;
  checked?: boolean;
}

interface ItemToDisplayProps {
  categoryName: string;
  items: Item[];
  onEdit: (item: Item) => void;
  onDelete: (itemId: string) => void;
  onToggleChecked: (itemId: string, checked: boolean) => void;
}

export const ItemToDisplay = ({
  categoryName,
  items,
  onEdit,
  onDelete,
  onToggleChecked,
}: ItemToDisplayProps) => {
  const [expanded, setExpanded] = useState(true);

  // Gestion de l'expansion des items de la catégorie
  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  return (
    <div className="mb-2">
      {/* En-tête de la catégorie */}
      <div
        className="flex items-center p-2 rounded-md hover:bg-base-200 cursor-pointer"
        onClick={toggleExpand}
      >
        {/* Icône d'expansion */}
        <div className="w-5 flex justify-center">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </div>

        {/* Icône de catégorie */}
        <div className="ml-1 mr-2">
          <ShoppingCart size={16} className="text-primary font-bold" />
        </div>

        {/* Nom de la catégorie */}
        <div className="flex-grow font-medium">{categoryName}</div>

        {/* Nombre d'articles */}
        <div className="badge badge-sm">{items.length}</div>
      </div>

      {/* Liste des articles de cette catégorie */}
      {expanded && (
        <div className="pl-6 ml-2 border-l border-dashed border-base-300">
          {items.map((item) => (
            <div
              key={item._id}
              className={`flex items-center p-2 rounded-md hover:bg-base-200 ${
                item.checked ? "opacity-50 line-through text-gray-500" : ""
              }`}
            >
              {/* Checkbox pour marquer comme acheté */}
              <div className="mr-2">
                <button
                  className={`btn btn-circle btn-xs ${
                    item.checked ? "btn-success" : "btn-outline"
                  }`}
                  onClick={() => onToggleChecked(item._id, !item.checked)}
                  aria-label={item.checked ? "Décocher" : "Cocher"}
                >
                  {item.checked && <Check size={12} />}
                </button>
              </div>

              {/* Nom de l'article */}
              <div className="flex-grow">{item.name}</div>

              {/* Actions */}
              <div className="flex gap-1">
                <button
                  className="btn btn-ghost btn-xs"
                  onClick={() => onEdit(item)}
                  aria-label="Modifier"
                >
                  <Edit size={14} />
                </button>
                <button
                  className="btn btn-ghost btn-xs text-error"
                  onClick={() => onDelete(item._id)}
                  aria-label="Supprimer"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
